import React, { useState, useEffect } from 'react';
import '../AssessmentForm.css';

/**
 * ConsentTabTriage
 * 
 * Handles consent for Triage report types (2002: Triage, 2003: Triage Follow Up)
 * 
 * This component is SEPARATE from ConsentTabNetworkEform because:
 * - Triage forms are PATIENT-FACING consent/intake forms
 * - They capture patient consent (ConsentGained) and data protection confirmation
 * - They require GP contact information for safety purposes
 * - Different fields than Network Eform (no DetailsChecked, no AssessmentDate, no SignedBy)
 * 
 * See: Swagger endpoints explicitly support these fields only for types 2002, 2003
 */
const ConsentTabTriage = ({ reportId, api, patientInfo, onSave , isPublished, reportType}) => {
  const [consentGained, setConsentGained] = useState(null);
  const [consentNotes, setConsentNotes] = useState('');
  const [virtualCallType, setVirtualCallType] = useState(null);
  const [dataProtectionCompleted, setDataProtectionCompleted] = useState(null);
  const [gpName, setGpName] = useState('');
  const [gpSurgery, setGpSurgery] = useState('');
  const [gpAddress, setGpAddress] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingStates, setSavingStates] = useState({});
  const [saveTimeouts, setSaveTimeouts] = useState({});
  const [initialCharCounts, setInitialCharCounts] = useState({}); // Track initial character count when typing starts

  const sectionName = reportType === 2003 ? 'psych-triage-followup-consent' : 'psych-triage-consent';

  useEffect(() => {
    loadConsentData();
  }, [reportId]);

  const loadConsentData = async () => {
    try {
      setLoading(true);
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/${sectionName}`);

      if (response.ok) {
        const data = await response.json();
        setConsentGained(data.consentGained);
        setConsentNotes(data.consentGainedNotes || '');
        setVirtualCallType(data.virtualCallType || null);
        setDataProtectionCompleted(data.dataProtectionCompleted);
        setGpName(data.gpName || '');
        setGpSurgery(data.gpSurgery || '');
        setGpAddress(data.gpAddress || '');

        // Set loaded fields as 'saved' to show green indicator
        const initialSavingStates = {};
        if (data.consentGained !== null) initialSavingStates['ConsentGained'] = 'saved';
        if (data.consentGainedNotes) initialSavingStates['ConsentGainedNotes'] = 'saved';
        if (data.virtualCallType) initialSavingStates['VirtualCallType'] = 'saved';
        if (data.dataProtectionCompleted !== null) initialSavingStates['DataProtectionCompleted'] = 'saved';
        if (data.gpName) initialSavingStates['GpName'] = 'saved';
        if (data.gpSurgery) initialSavingStates['GpSurgery'] = 'saved';
        if (data.gpAddress) initialSavingStates['GpAddress'] = 'saved';
        setSavingStates(initialSavingStates);
      }
    } catch (error) {
      console.error('Error loading consent data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveField = async (fieldName, value) => {
    if (isPublished) return;
    setSavingStates(prev => ({ ...prev, [fieldName]: 'saving' }));

    try {
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/${sectionName}/fields/${fieldName}`, {
        method: 'PATCH',
        body: JSON.stringify(value)
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      setSavingStates(prev => ({ ...prev, [fieldName]: 'saved' }));
      if (onSave) onSave();

      // Keep saved indicator - don't clear it
    } catch (error) {
      console.error(`Error saving ${fieldName}:`, error);
      setSavingStates(prev => ({ ...prev, [fieldName]: 'error' }));
    }
  };

  const getSaveIndicator = (fieldName) => {
    const state = savingStates[fieldName];

    // Check if field has a valid value - don't show indicator for empty fields
    const fieldValues = {
      'ConsentGained': consentGained,
      'ConsentGainedNotes': consentNotes,
      'VirtualCallType': virtualCallType,
      'DataProtectionCompleted': dataProtectionCompleted,
      'GpName': gpName,
      'GpSurgery': gpSurgery,
      'GpAddress': gpAddress
    };

    const fieldValue = fieldValues[fieldName];
    const hasValue = fieldValue !== null && fieldValue !== undefined && fieldValue !== '';

    // Always return a span to allocate space
    if (state === 'saving' && hasValue) {
      return (
        <span className="save-status saving" aria-label="Saving">
          <span className="spinner-small"></span>
        </span>
      );
    }
    if (state === 'saved' && hasValue) {
      return (
        <span className="save-status saved" aria-label="Saved">
          <span className="checkmark">✓</span>
        </span>
      );
    }
    if (state === 'error') {
      return (
        <span className="save-status error" aria-label="Error saving">
          <span className="error-mark">⚠</span>
        </span>
      );
    }
    // Return empty span to maintain space
    return <span className="save-status" aria-hidden="true"></span>;
  };

  const getCharacterCounter = (currentLength, maxLength) => {
    if (!maxLength) return null;
    const remaining = maxLength - currentLength;
    const isNearLimit = remaining <= 50;
    return (
      <span className={`character-counter ${isNearLimit ? 'near-limit' : ''}`}>
        {currentLength} / {maxLength} characters
      </span>
    );
  };

  const handleConsentResponse = (granted) => {
    setConsentGained(granted);
    saveField('ConsentGained', granted);
  };

  const handleFieldChange = (fieldName, value, setter) => {
    setter(value);

    // Track initial character count if not already tracking
    if (initialCharCounts[fieldName] === undefined) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
    }

    // Calculate characters typed since start
    const charsSinceStart = Math.abs(value.length - (initialCharCounts[fieldName] || 0));

    // Clear existing timeout
    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    // Don't show saving indicator yet - keep previous state
    // Only trigger save if 50+ characters typed
    if (charsSinceStart >= 50) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
      return;
    }

    // Debounce save - 10 seconds after typing stops
    const timeoutId = setTimeout(() => {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
    }, 10000); // 10 seconds after typing stops

    setSaveTimeouts(prev => ({ ...prev, [fieldName]: timeoutId }));
  };

  const handleFieldBlur = (fieldName, value) => {
    // Clear debounce timeout on blur
    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    // Reset character count for next typing session
    setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));

    // Save immediately on blur
    saveField(fieldName, value);
  };

  const handleNotesChange = (e) => {
    const value = e.target.value;
    handleFieldChange('ConsentGainedNotes', value, setConsentNotes);
  };

  if (loading) {
    return <div className="loading-state"><div className="spinner"></div><p>Loading consent information...</p></div>;
  }

  return (
      <div className="assessment-container">
      <fieldset disabled={isPublished} style={{ border: "none", padding: 0, margin: 0 }}>
          <div className="consent-section">
              {consentGained === null ? (
                  <div className="consent-form">
                      {patientInfo?.isChildAtReferral && (
                          <div className="child-consent-warning">
                            <h3>CYP Consent Warning</h3>
                              <strong>
                                  If assessing a patient under the age of 18,
                                  ensure there is a parent present for the
                                  consent script and gain consent from both
                                  parent and child if the child is deemed to be
                                  Gillick competent.
                              </strong>
                          </div>
                      )}
                      <h3>
                          Consent Script – To Be Read During Triage / Initial
                          Assessment Call
                      </h3>
                      <p>
                          To proceed with your referral today, I need to ask you
                          some medical questions regarding your symptoms in
                          order to determine the most appropriate treatment
                          pathway for you. Are you happy to continue with your
                          assessment now?
                      </p>
                      <ol>
                          <li>
                              If at any stage during your treatment we are
                              concerned about your safety or the safety of
                              others, we will speak with you and seek your
                              consent to contact your GP or other appropriate
                              services.
                          </li>
                          <li>
                              However, if we believe there is a serious risk, we
                              may be obliged to contact the relevant services
                              even without your consent.
                          </li>
                          <li>
                              For your information, IPRS Health has a Patient
                              Privacy Notice that explains how and why we
                              collect, use, and store your data. This can be
                              accessed on our website at www.iprshealth.com. It
                              contains important information, and we encourage
                              you to review it carefully.
                          </li>
                          <li>
                              In order to provide you with the most suitable
                              care, we may need to share your personal data with
                              third-party clinical providers. This is strictly
                              for the purpose of delivering appropriate
                              treatment.
                          </li>
                          <li>
                              For example, if a referral to a psychiatrist is
                              required, we will need to share relevant
                              information with them to ensure continuity of
                              care.
                          </li>
                      </ol>

                      <div className="consent-notes">
                          <label htmlFor="consentNotes">
                              Consent Notes:
                              {getSaveIndicator("ConsentGainedNotes")}
                          </label>
                          <textarea
                              id="consentNotes"
                              value={consentNotes}
                              onChange={handleNotesChange}
                              onBlur={() =>
                                  handleFieldBlur(
                                      "ConsentGainedNotes",
                                      consentNotes
                                  )
                              }
                              rows="4"
                              maxLength="1000"
                              style={{ width: "100%" }}
                          />
                          {getCharacterCounter(consentNotes.length, 1000)}
                      </div>

                      <div className="consent-buttons">
                          <button
                              className="consent-button yes"
                              onClick={() => handleConsentResponse(true)}
                          >
                              Yes
                          </button>
                          <button
                              className="consent-button no"
                              onClick={() => handleConsentResponse(false)}
                          >
                              No
                          </button>
                      </div>
                  </div>
              ) : consentGained === false ? (
                  <div className="consent-denied">
                      <h3>Consent Declined</h3>
                      <p>
                          The patient has declined consent. Treatment cannot
                          proceed without consent.
                      </p>
                      <p>
                          <strong>Notes:</strong> {consentNotes}
                      </p>
                      {!isPublished && (
                          <button
                              className="consent-button"
                              onClick={() => setConsentGained(null)}
                          >
                              Re-edit Consent
                          </button>
                      )}
                  </div>
              ) : (
                  <div className="consent-granted">
                      <h3>✓ Consent Granted</h3>
                      <p>
                          <strong>Notes:</strong> {consentNotes}
                      </p>
                      {!isPublished && (
                          <button
                              className="consent-button"
                              onClick={() => setConsentGained(null)}
                          >
                              Re-edit Consent
                          </button>
                      )}
                  </div>
              )}
          </div>

          <div className="virtual-call-type-section">
              <label className="inline-label">
                  <strong>Virtual Call Type</strong>
                  {getSaveIndicator("VirtualCallType")}
              </label>
              <div className="radio-group-inline">
                  <label>
                      <input
                          type="radio"
                          name="virtualCallType"
                          checked={virtualCallType === 'Telephone Call'}
                          onChange={() => {
                              setVirtualCallType('Telephone Call');
                              saveField('VirtualCallType', 'Telephone Call');
                          }}
                          required
                      />
                      Telephone Call
                  </label>
                  <label>
                      <input
                          type="radio"
                          name="virtualCallType"
                          checked={virtualCallType === 'Video Call'}
                          onChange={() => {
                              setVirtualCallType('Video Call');
                              saveField('VirtualCallType', 'Video Call');
                          }}
                          required
                      />
                      Video Call
                  </label>
              </div>
              {!virtualCallType && (
                  <div className="validation-error">
                      data required for client output
                  </div>
              )}
          </div>

          <div className="data-protection-section">
              <label className="inline-label">
                  <strong>Data Protection completed?</strong>
              </label>
              <div className="radio-group-inline">
                  <label>
                      <input
                          type="radio"
                          name="dataProtection"
                          checked={dataProtectionCompleted === true}
                          onChange={() => {
                              setDataProtectionCompleted(true);
                              saveField("DataProtectionCompleted", true);
                          }}
                      />
                      Yes
                  </label>
                  <label>
                      <input
                          type="radio"
                          name="dataProtection"
                          checked={dataProtectionCompleted === false}
                          onChange={() => {
                              setDataProtectionCompleted(false);
                              saveField("DataProtectionCompleted", false);
                          }}
                      />
                      No
                  </label>
              </div>
          </div>

          <div className="gp-details-section">
              <h3>
                  Take GP details. If we are concerned for your safety at any
                  time during treatment we will discuss this with you and
                  request your consent to contact your GP or other services. If
                  we thought that there was risk involved then we would contact
                  the relevant parties even if consent was denied.
              </h3>

              <div className="form-field">
                  <label htmlFor="gpName">
                      GP Name
                      {getSaveIndicator("GpName")}
                  </label>
                  <input
                      type="text"
                      id="gpName"
                      value={gpName}
                      onChange={e =>
                          handleFieldChange("GpName", e.target.value, setGpName)
                      }
                      onBlur={e => handleFieldBlur("GpName", e.target.value)}
                      maxLength="100"
                      required
                  />
                  {getCharacterCounter(gpName.length, 100)}
                  {!gpName && (
                      <div className="validation-error">
                          data required for client output
                      </div>
                  )}
              </div>

              <div className="form-field">
                  <label htmlFor="gpSurgery">
                      GP Surgery
                      {getSaveIndicator("GpSurgery")}
                  </label>
                  <input
                      type="text"
                      id="gpSurgery"
                      value={gpSurgery}
                      onChange={e =>
                          handleFieldChange(
                              "GpSurgery",
                              e.target.value,
                              setGpSurgery
                          )
                      }
                      onBlur={e => handleFieldBlur("GpSurgery", e.target.value)}
                      maxLength="100"
                      required
                  />
                  {getCharacterCounter(gpSurgery.length, 100)}
                  {!gpSurgery && (
                      <div className="validation-error">
                          data required for client output
                      </div>
                  )}
              </div>

              <div className="form-field">
                  <label htmlFor="gpAddress">
                      GP Address
                      {getSaveIndicator("GpAddress")}
                  </label>
                  <textarea
                      id="gpAddress"
                      value={gpAddress}
                      onChange={e =>
                          handleFieldChange(
                              "GpAddress",
                              e.target.value,
                              setGpAddress
                          )
                      }
                      onBlur={e => handleFieldBlur("GpAddress", e.target.value)}
                      rows="5"
                      maxLength="500"
                      required
                  />
                  {getCharacterCounter(gpAddress.length, 500)}
                  {!gpAddress && (
                      <div className="validation-error">
                          data required for client output
                      </div>
                  )}
              </div>
          </div>
      </fieldset>

      </div>
  );
};

export default ConsentTabTriage;

